/**
 * SimpleImagePanel - Floats museum artwork in 3D space beside the
 * character's head while the legend speaks about it.
 *
 * Task #403 rework — museum presentation:
 *  - HEAD-ANCHORED placement: recomputed on every show from the same
 *    eye/head detection chain the camera framing uses (explicit
 *    EyeAnchor → eye nodes → head-bone/mesh/upper-third fallbacks),
 *    so the piece hangs beside the head for every character — tall,
 *    short, GLB or FBX — and character swaps stay correct.
 *  - Sized from the character's face scale (not a fixed 80 units) and
 *    fitted to the live camera frustum, so the default portrait
 *    framing shows it on desktop and mobile without any camera move.
 *    Wide landscape images are pushed out by half their width so the
 *    near edge keeps clearance from the face.
 *  - Gallery chrome: bronze frame + antique-gold fillet + a small
 *    cream placard rendering the artwork's caption beneath it.
 *  - Rise-and-settle reveal, sink dismissal; after the legend finishes
 *    speaking the piece lingers briefly then takes its bow (manual
 *    close — ESC / 3D × button — always works).
 *  - Camera gating preserved: image shows only while the camera is on
 *    the front side of the plane (orbit behind → hidden).
 *  - NO partial opacity: materials stay fully opaque (no blending
 *    artifacts against the character) — all motion is transform-only.
 *  - NO internal rAF loop: the shared render loop already steps
 *    `imagePanel.update()` every frame while visible.
 */
import * as THREE from "three";
import { computeArtworkLayout } from "./ArtworkPanelPlacement.js";

// Linger-then-fade after narration: only once we have heard real
// speech (window.TTSActive, the module-global "audio is audible"
// flag) for at least MIN_SPEECH, then silence for SETTLE, does the
// LINGER countdown start. If narration never happens, the artwork
// simply stays until manually closed.
const AUTO_HIDE_MIN_SPEECH_MS = 1200;
const AUTO_HIDE_SPEECH_SETTLE_MS = 2500;
const AUTO_HIDE_LINGER_MS = 7000;

export class SimpleImagePanel {
    constructor(scene, camera, opts = {}) {
        this.scene = scene;
        this.camera = camera;

        // Provider that recomputes the per-character head anchor on
        // EVERY show (injected by ModelViewer). Null → legacy
        // fixed-position fallback below.
        this.getPlacementAnchor =
            opts && typeof opts.getPlacementAnchor === "function"
                ? opts.getPlacementAnchor
                : null;

        this.isVisible = false; // logical flag: user asked to show image
        this.group = null; // root: art + frame + fillet + placard + close
        this.plane = null; // the art mesh itself
        this.texture = null;
        this.material = null;
        this.frameMesh = null;
        this.filletMesh = null;
        this.placardMesh = null;
        this._placardTexture = null;

        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();

        // Legacy fallback (only used when no head anchor is available,
        // e.g. no model committed yet).
        this.panelHeight = 80;
        this.panelPosition = new THREE.Vector3(-70, 175, -55);

        this.closeButton = null;

        // Track currently displayed image URL (slideshow de-dupe)
        this._currentImageUrl = null;
        this._currentLayout = null;
        this._animToken = 0; // cancels stale show/hide animations
        this._autoHide = null;

        // Scratch objects (no per-frame allocation)
        this._q = new THREE.Quaternion();
        this._v1 = new THREE.Vector3();
        this._v2 = new THREE.Vector3();
        this._v3 = new THREE.Vector3();

        this.createMaterials();
        this.setupKeyboardEvents();
        this.setupClickHandler();
    }

    /**
     * Helper: create rounded-rectangle geometry
     */
    createRoundedRectGeometry(width, height, radius, segments = 8) {
        const shape = new THREE.Shape();
        const hw = width / 2;
        const hh = height / 2;
        const r = Math.min(radius, hw, hh);

        // Rounded rectangle path
        shape.moveTo(-hw + r, hh);
        shape.lineTo(hw - r, hh);
        shape.quadraticCurveTo(hw, hh, hw, hh - r);
        shape.lineTo(hw, -hh + r);
        shape.quadraticCurveTo(hw, -hh, hw - r, -hh);
        shape.lineTo(-hw + r, -hh);
        shape.quadraticCurveTo(-hw, -hh, -hw, -hh + r);
        shape.lineTo(-hw, hh - r);
        shape.quadraticCurveTo(-hw, hh, -hw + r, hh);

        const geometry = new THREE.ShapeGeometry(shape, segments);

        // Generate UVs based on bounding box
        geometry.computeBoundingBox();
        const bbox = geometry.boundingBox;
        const max = bbox.max;
        const min = bbox.min;
        const range = new THREE.Vector2(max.x - min.x, max.y - min.y);

        const uvAttr = geometry.attributes.uv;
        const posAttr = geometry.attributes.position;
        for (let i = 0; i < uvAttr.count; i++) {
            const x = posAttr.getX(i);
            const y = posAttr.getY(i);
            uvAttr.setXY(i, (x - min.x) / range.x, (y - min.y) / range.y);
        }
        uvAttr.needsUpdate = true;

        return geometry;
    }

    /**
     * Create the group, art plane, and gallery-frame meshes.
     * (Placard is rebuilt per show — it depends on the caption.)
     */
    createMaterials() {
        this.group = new THREE.Group();
        this.group.visible = false;
        this.scene.add(this.group);

        this.material = new THREE.MeshBasicMaterial({
            transparent: false, // fully opaque, no alpha blending
            opacity: 1,
            side: THREE.DoubleSide,
            depthWrite: true, // writes to depth buffer normally
            depthTest: true, // respects objects in front of it
        });

        const planeGeometry = this.createRoundedRectGeometry(1, 1, 0.1);
        this.plane = new THREE.Mesh(planeGeometry, this.material);

        // Draw late so it behaves like an overlay, but depthTest will
        // keep it behind the model
        this.plane.renderOrder = 9000;
        this.group.add(this.plane);

        // Gallery frame: deep bronze outer border…
        this.frameMaterial = new THREE.MeshBasicMaterial({
            color: 0x2a211b,
            side: THREE.DoubleSide,
        });
        this.frameMesh = new THREE.Mesh(
            this.createRoundedRectGeometry(1.1, 1.1, 0.12),
            this.frameMaterial,
        );
        this.frameMesh.position.z = -0.5;
        this.frameMesh.renderOrder = 8997;
        this.group.add(this.frameMesh);

        // …with a thin antique-gold fillet between frame and art.
        this.filletMaterial = new THREE.MeshBasicMaterial({
            color: 0xb08d57,
            side: THREE.DoubleSide,
        });
        this.filletMesh = new THREE.Mesh(
            this.createRoundedRectGeometry(1.05, 1.05, 0.11),
            this.filletMaterial,
        );
        this.filletMesh.position.z = -0.25;
        this.filletMesh.renderOrder = 8998;
        this.group.add(this.filletMesh);
    }

    /**
     * Create or update the 3D close button attached to the image
     * Top-left corner of the image.
     */
    createOrUpdateCloseButton() {
        if (!this.plane) return;

        const width = this.plane.geometry.boundingBox
            ? this.plane.geometry.boundingBox.max.x -
              this.plane.geometry.boundingBox.min.x
            : this.plane.geometry.parameters.width || 1;

        const height = this.plane.geometry.boundingBox
            ? this.plane.geometry.boundingBox.max.y -
              this.plane.geometry.boundingBox.min.y
            : this.plane.geometry.parameters.height || 1;

        if (!this.closeButton) {
            this.closeButton = new THREE.Group();

            // ==========================
            // 1) SOLID DARK BACKGROUND
            //    rgba(58,65,73, 1)
            // ==========================
            const radius = 2.5;
            const circleGeo = new THREE.CircleGeometry(radius, 64);
            const circleMat = new THREE.MeshBasicMaterial({
                color: 0x3a4149, // #3A4149
                transparent: false,
                opacity: 1, // fully solid
                depthTest: false,
                depthWrite: false,
            });
            const circleMesh = new THREE.Mesh(circleGeo, circleMat);
            circleMesh.position.set(0, 0, 0);
            this.closeButton.add(circleMesh);

            // ==========================
            // 2) BORDER STROKE
            // ==========================
            const ringGeo = new THREE.RingGeometry(radius - 0.2, radius, 64);
            const ringMat = new THREE.MeshBasicMaterial({
                color: 0xffffff, // white border
                transparent: false,
                opacity: 0.7,
                depthTest: false,
                depthWrite: false,
            });
            const ringMesh = new THREE.Mesh(ringGeo, ringMat);
            ringMesh.position.z = 0.01;
            this.closeButton.add(ringMesh);

            // ==========================
            // 3) WHITE "X"
            // ==========================
            const barLength = 2;
            const barThickness = 0.3; // Slightly thicker for readability
            const barGeo = new THREE.PlaneGeometry(barLength, barThickness);

            const barMat = new THREE.MeshBasicMaterial({
                color: 0xffffff, // PURE WHITE X
                transparent: false,
                opacity: 1,
                depthTest: false,
                depthWrite: false,
            });

            const bar1 = new THREE.Mesh(barGeo, barMat);
            const bar2 = new THREE.Mesh(barGeo, barMat);

            bar1.rotation.z = Math.PI / 4;
            bar2.rotation.z = -Math.PI / 4;

            bar1.position.set(0, 0, 0.02);
            bar2.position.set(0, 0, 0.02);

            this.closeButton.add(bar1);
            this.closeButton.add(bar2);

            // ==========================
            // 4) RENDER ORDER / SCALE
            // ==========================
            circleMesh.renderOrder = 9998;
            ringMesh.renderOrder = 9999;
            bar1.renderOrder = 10000;
            bar2.renderOrder = 10000;

            this.plane.add(this.closeButton);
        }

        // Scale the button with the art so it doesn't dwarf a small
        // (mobile-fitted) piece.
        const k = Math.min(
            1.2,
            Math.max(
                0.65,
                (this._currentLayout ? this._currentLayout.height : 80) / 80,
            ),
        );
        this.closeButton.scale.set(0.95 * k, 0.95 * k, 1);

        // ==========================
        // POSITION TOP LEFT
        // ==========================
        const x = -width / 2 + 4 * k;
        const y = height / 2 - 4 * k;

        this.closeButton.position.set(x, y, 0.01);
        this.closeButton.visible = true;
    }

    /**
     * Decide if camera is in front or behind the panel.
     * Returns true if camera is in front (we should show the image),
     * false if camera is behind (we hide it).
     */
    isCameraInFront() {
        if (!this.group || !this.camera) return true;

        // Panel normal in world space (group children face local +Z;
        // the group carries the per-character yaw).
        this.group.getWorldQuaternion(this._q);
        const planeNormal = this._v1
            .set(0, 0, 1)
            .applyQuaternion(this._q)
            .normalize();

        // Direction from panel to camera
        this.group.getWorldPosition(this._v2);
        const toCamera = this._v3
            .subVectors(this.camera.position, this._v2)
            .normalize();

        const dot = planeNormal.dot(toCamera); // 1 = front, -1 = back

        // Threshold: adjust if needed
        const FRONT_THRESHOLD = 0.4;
        return dot > FRONT_THRESHOLD;
    }

    /**
     * Show an image in 3D space beside the character's head.
     * @param {string} imageUrl
     * @param {object} [opts]  { caption, autoHideAfterSpeech }
     */
    async showImage(imageUrl, opts = {}) {
        try {
            console.log("SimpleImagePanel: Loading image texture:", imageUrl);

            const texture = await this.loadTexture(imageUrl);

            if (!texture || !texture.image) {
                console.error("Failed to load texture or image is missing");
                return false;
            }

            this.texture = texture;
            // Renderer outputs sRGB — tag the art so gallery colors
            // are faithful instead of washed out.
            try {
                if (THREE.SRGBColorSpace) {
                    texture.colorSpace = THREE.SRGBColorSpace;
                }
            } catch (_e) {
                /* older three — leave default */
            }

            this._presentTexture(texture, imageUrl, opts || {});
            return true;
        } catch (error) {
            console.error("Error showing image in 3D space:", error);
            return false;
        }
    }

    /**
     * Synchronous presentation of a loaded texture: layout, meshes,
     * placard, reveal. Split from showImage so the placement logic is
     * testable without a network/texture pipeline.
     */
    _presentTexture(texture, imageUrl, opts = {}) {
        this.material.map = texture;
        this.material.needsUpdate = true;

        const imgWidth = texture.image.width;
        const imgHeight = texture.image.height;
        const aspectRatio = imgHeight > 0 ? imgWidth / imgHeight : 16 / 9;

        // Fresh per-character layout on EVERY show (character swaps
        // reposition automatically; slideshow inherits it too).
        const layout = this._computeLayout(aspectRatio);
        this._currentLayout = layout;

        this._rebuildMeshes(layout, (opts.caption || "").toString());

        // Logical visibility flag
        this.isVisible = true;
        this._currentImageUrl = imageUrl;

        // Defensive: if something rebuilt the scene, re-adopt the group.
        if (this.group.parent !== this.scene) {
            this.scene.add(this.group);
        }

        this.group.position.copy(layout.position);
        this.group.rotation.set(0, layout.rotationY, 0);

        this.createOrUpdateCloseButton();
        this.showCloseHint();
        this._armAutoHide(opts);
        this.animateShow(layout);

        return layout;
    }

    /**
     * Head-anchored layout via the provider; legacy fixed position
     * when no anchor is available.
     */
    _computeLayout(imageAspect) {
        let anchor = null;
        if (this.getPlacementAnchor) {
            try {
                anchor = this.getPlacementAnchor();
            } catch (e) {
                console.warn("[ArtworkPanel] anchor provider failed", e);
            }
        }
        if (anchor) {
            const layout = computeArtworkLayout(
                anchor,
                imageAspect,
                this.camera,
            );
            if (layout) {
                console.log(
                    `[ArtworkPanel] beside head (source=${layout.anchorSource}, ` +
                        `h=${layout.height.toFixed(1)}, ` +
                        `fit=${layout.fitScale.toFixed(2)}` +
                        `${layout.tucked ? ", tucked" : ""}` +
                        `${layout.overflow ? ", overflow" : ""})`,
                );
                return layout;
            }
        }

        console.warn(
            "[ArtworkPanel] no head anchor — using legacy fixed position",
        );
        const height = this.panelHeight;
        return {
            position: this.panelPosition.clone(),
            rotationY: 0,
            height,
            width: height * imageAspect,
            frame: Math.min(4.5, Math.max(1.6, height * 0.045)),
            fillet: Math.max(0.6, height * 0.012),
            placardHeight: Math.min(12, Math.max(6, height * 0.16)),
            placardGap: Math.min(5, Math.max(1.5, height * 0.05)),
            clearance: 0,
            depth: 0,
            tucked: false,
            overflow: false,
            fitScale: 1,
            screenSide: "left",
            anchorSource: "legacy-fixed",
        };
    }

    /**
     * Rebuild art / frame / fillet / placard geometry for a layout.
     */
    _rebuildMeshes(layout, caption) {
        const w = layout.width;
        const h = layout.height;
        const cornerRadius = Math.min(w, h) * 0.04;

        if (this.plane.geometry) this.plane.geometry.dispose();
        this.plane.geometry = this.createRoundedRectGeometry(
            w,
            h,
            cornerRadius,
            12,
        );
        this.plane.geometry.computeBoundingBox();

        if (this.frameMesh.geometry) this.frameMesh.geometry.dispose();
        this.frameMesh.geometry = this.createRoundedRectGeometry(
            w + 2 * layout.frame,
            h + 2 * layout.frame,
            cornerRadius + layout.frame * 0.7,
            12,
        );

        const filletBorder = layout.fillet * 2.2;
        if (this.filletMesh.geometry) this.filletMesh.geometry.dispose();
        this.filletMesh.geometry = this.createRoundedRectGeometry(
            w + 2 * filletBorder,
            h + 2 * filletBorder,
            cornerRadius + filletBorder * 0.7,
            12,
        );

        this._rebuildPlacard(layout, caption);
        this.group.scale.set(1, 1, 1);
    }

    /**
     * Small cream placard plate beneath the frame with the caption.
     * Skipped entirely when there is no caption (e.g. slideshow).
     */
    _rebuildPlacard(layout, caption) {
        if (this.placardMesh) {
            this.group.remove(this.placardMesh);
            if (this.placardMesh.geometry) this.placardMesh.geometry.dispose();
            if (this.placardMesh.material) this.placardMesh.material.dispose();
            this.placardMesh = null;
        }
        if (this._placardTexture) {
            this._placardTexture.dispose();
            this._placardTexture = null;
        }

        const text = (caption || "").trim();
        if (!text) return;

        const pw = Math.min(
            layout.width + 2 * layout.frame,
            Math.max(layout.width * 0.62, 26),
        );
        const ph = layout.placardHeight;
        const tex = this._buildPlacardTexture(text, pw, ph);
        if (!tex) return;

        this._placardTexture = tex;
        const mat = new THREE.MeshBasicMaterial({
            map: tex,
            side: THREE.DoubleSide,
        });
        const geo = this.createRoundedRectGeometry(pw, ph, ph * 0.16, 8);
        this.placardMesh = new THREE.Mesh(geo, mat);
        this.placardMesh.position.set(
            0,
            -layout.height / 2 -
                layout.frame -
                layout.placardGap -
                ph / 2,
            0,
        );
        this.placardMesh.renderOrder = 9000;
        this.group.add(this.placardMesh);
    }

    /**
     * Render the placard plate + caption text into a canvas texture.
     * Returns null when a 2D context is unavailable (the placard is
     * simply skipped — never fatal).
     */
    _buildPlacardTexture(text, worldW, worldH) {
        try {
            if (typeof document === "undefined") return null;
            const scale = 22; // px per world unit (cm)
            const w = Math.round(Math.min(1600, Math.max(220, worldW * scale)));
            const h = Math.round(Math.min(320, Math.max(60, worldH * scale)));
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext ? canvas.getContext("2d") : null;
            if (!ctx) return null;

            // Warm gallery cream plate with a fine bronze inset rule.
            ctx.fillStyle = "#f5eee0";
            ctx.fillRect(0, 0, w, h);
            const inset = Math.max(2, h * 0.07);
            ctx.strokeStyle = "rgba(122, 98, 62, 0.55)";
            ctx.lineWidth = Math.max(1, h * 0.015);
            ctx.strokeRect(inset, inset, w - inset * 2, h - inset * 2);

            ctx.fillStyle = "#2c2418";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            const family = 'Fraunces, Georgia, "Times New Roman", serif';
            const maxTextW = w - inset * 2 - h * 0.5;
            let fontPx = h * 0.32;
            const setFont = (px) => {
                ctx.font = `italic 500 ${Math.max(9, Math.round(px))}px ${family}`;
            };
            setFont(fontPx);
            let wrapped = this._wrapPlacardLines(ctx, text, maxTextW, 2);
            if (wrapped.tooWide) {
                fontPx *= 0.78;
                setFont(fontPx);
                wrapped = this._wrapPlacardLines(ctx, text, maxTextW, 2);
            }
            const lines = wrapped.lines;
            const lineH = fontPx * 1.25;
            const cy = h / 2 - ((lines.length - 1) * lineH) / 2;
            lines.forEach((line, i) => {
                ctx.fillText(line, w / 2, cy + i * lineH);
            });

            const tex = new THREE.CanvasTexture(canvas);
            try {
                if (THREE.SRGBColorSpace) tex.colorSpace = THREE.SRGBColorSpace;
            } catch (_e) {
                /* noop */
            }
            tex.anisotropy = 4;
            return tex;
        } catch (e) {
            console.warn("[ArtworkPanel] placard texture failed", e);
            return null;
        }
    }

    /** Word-wrap caption into ≤ maxLines, ellipsizing overflow. */
    _wrapPlacardLines(ctx, text, maxW, maxLines) {
        const words = text.split(/\s+/).filter(Boolean);
        const lines = [];
        let cur = "";
        let overflowed = false;
        for (let i = 0; i < words.length; i++) {
            const word = words[i];
            const cand = cur ? cur + " " + word : word;
            if (ctx.measureText(cand).width <= maxW || !cur) {
                cur = cand;
            } else {
                if (lines.length === maxLines - 1) {
                    overflowed = true;
                    break;
                }
                lines.push(cur);
                cur = word;
            }
        }
        if (cur) lines.push(cur);
        if (overflowed && lines.length) {
            let last = lines[lines.length - 1];
            while (
                last.length > 1 &&
                ctx.measureText(last + "…").width > maxW
            ) {
                last = last.slice(0, -1).trimEnd();
            }
            lines[lines.length - 1] = last + "…";
        }
        const clipped = lines.slice(0, maxLines);
        const tooWide = clipped.some(
            (l) => ctx.measureText(l).width > maxW,
        );
        return { lines: clipped, tooWide };
    }

    /**
     * Small DOM hint at bottom of screen
     */
    showCloseHint() {
        const hintElement = document.createElement("div");
        hintElement.className = "image-close-hint";
        hintElement.innerHTML =
            "Press ESC or tap the × on the artwork to close";
        hintElement.style.cssText = `
            position: fixed;
            bottom: 20px;
            left: 50%;
            transform: translateX(-50%);
            background-color: rgba(0, 0, 0, 0.7);
            color: white;
            padding: 8px 16px;
            border-radius: 20px;
            font-size: 14px;
            z-index: 1000;
            opacity: 0;
            transition: opacity 0.5s ease;
        `;

        document.body.appendChild(hintElement);

        setTimeout(() => {
            hintElement.style.opacity = "1";
        }, 100);
        setTimeout(() => {
            hintElement.style.opacity = "0";
            setTimeout(() => {
                if (hintElement.parentNode) {
                    hintElement.parentNode.removeChild(hintElement);
                }
            }, 500);
        }, 5000);
    }

    /**
     * Arm / disarm the linger-then-fade watcher for this show.
     */
    _armAutoHide(opts) {
        if (opts && opts.autoHideAfterSpeech) {
            this._autoHide = {
                lastTick:
                    typeof performance !== "undefined"
                        ? performance.now()
                        : Date.now(),
                speechMs: 0,
                lastSpeechAt: 0,
                hideAt: 0,
            };
        } else {
            this._autoHide = null;
        }
    }

    /** Stepped from update(): linger briefly after narration, then bow. */
    _tickAutoHide() {
        const ah = this._autoHide;
        if (!ah) return;
        const now =
            typeof performance !== "undefined"
                ? performance.now()
                : Date.now();
        const dt = Math.min(200, Math.max(0, now - ah.lastTick));
        ah.lastTick = now;

        const speaking =
            typeof window !== "undefined" && !!window.TTSActive;
        if (speaking) {
            ah.speechMs += dt;
            ah.lastSpeechAt = now;
            ah.hideAt = 0; // a new sentence resets any pending bow
            return;
        }
        // Never auto-hide unless we actually heard the narration.
        if (ah.speechMs < AUTO_HIDE_MIN_SPEECH_MS || !ah.lastSpeechAt) return;
        // Debounce inter-sentence gaps before starting the linger.
        if (now - ah.lastSpeechAt < AUTO_HIDE_SPEECH_SETTLE_MS) return;
        if (!ah.hideAt) {
            ah.hideAt = now + AUTO_HIDE_LINGER_MS;
            return;
        }
        if (now >= ah.hideAt) {
            this._autoHide = null;
            if (!this.isSlideshowActive()) {
                console.log(
                    "[ArtworkPanel] narration ended — artwork takes its bow",
                );
                this.hideImage();
            }
        }
    }

    /**
     * Reveal: gentle rise-and-settle (transform only — materials stay
     * fully opaque).
     */
    animateShow(layout) {
        if (!this.group) return;
        const token = ++this._animToken;

        // Camera gating: only show if in front
        this.group.visible = this.isCameraInFront();

        const targetY = layout.position.y;
        const rise = Math.max(5, layout.height * 0.14);
        const duration = 700;
        const startTime =
            typeof performance !== "undefined" ? performance.now() : 0;

        this.group.position.y = targetY - rise;
        this.group.scale.set(0.94, 0.94, 0.94);

        // Soft overshoot on the rise = the "settle".
        const backOut = (t) => {
            const s = 1.1;
            const u = t - 1;
            return 1 + (s + 1) * u * u * u + s * u * u;
        };
        const easeOut = (t) => 1 - Math.pow(1 - t, 3);

        const finish = () => {
            this.group.position.y = targetY;
            this.group.scale.set(1, 1, 1);
        };

        if (typeof requestAnimationFrame !== "function") {
            finish();
            return;
        }

        const step = (time) => {
            if (token !== this._animToken) return; // superseded
            const t = Math.min((time - startTime) / duration, 1);
            this.group.position.y = targetY - rise * (1 - backOut(t));
            const sc = 0.94 + 0.06 * easeOut(t);
            this.group.scale.set(sc, sc, sc);
            if (t < 1 && this.isVisible) {
                requestAnimationFrame(step);
            } else {
                finish();
            }
        };
        requestAnimationFrame(step);
    }

    /**
     * Dismissal: sink-and-shrink, then hide (no opacity fade).
     */
    animateHide() {
        if (!this.group) return;
        const token = ++this._animToken;

        const startY = this.group.position.y;
        const sink = this._currentLayout
            ? Math.max(4, this._currentLayout.height * 0.1)
            : 8;
        const startScale = this.group.scale.x || 1;
        const duration = 300;
        const startTime =
            typeof performance !== "undefined" ? performance.now() : 0;

        const finish = () => {
            this.group.visible = false;
            this.group.scale.set(1, 1, 1);
            this.group.position.y = startY;
        };

        if (typeof requestAnimationFrame !== "function") {
            finish();
            return;
        }

        const easeIn = (t) => t * t * t;
        const step = (time) => {
            if (token !== this._animToken) return; // superseded
            const t = Math.min((time - startTime) / duration, 1);
            const e = easeIn(t);
            const sc = Math.max(0.001, startScale * (1 - e));
            this.group.scale.set(sc, sc, sc);
            this.group.position.y = startY - sink * e;
            if (t < 1) {
                requestAnimationFrame(step);
            } else {
                finish();
            }
        };
        requestAnimationFrame(step);
    }

    /**
     * Hide the panel (user request)
     */
    hideImage() {
        if (!this.isVisible) return;

        console.log("Image panel hidden by user request");
        this.isVisible = false;

        // Clear current image URL tracking
        this._currentImageUrl = null;
        this._autoHide = null;

        // Animate hide (transform only, no opacity fade)
        this.animateHide();
    }

    /**
     * Per-frame update — stepped by the SHARED render loop
     * (ModelViewer.animate) while logically visible. No internal rAF.
     * Toggles visibility based on camera side (front/back) and runs
     * the linger-then-fade watcher.
     */
    update() {
        if (!this.isVisible || !this.group) return;

        this.group.visible = this.isCameraInFront();
        this._tickAutoHide();
    }

    /**
     * Load a texture with CORS handling
     */
    loadTexture(url) {
        return new Promise((resolve, reject) => {
            const loader = new THREE.TextureLoader();
            loader.crossOrigin = "anonymous";

            const proxyUrl =
                url.startsWith("http") &&
                !url.includes(window.location.hostname)
                    ? `/api/proxy-image?url=${encodeURIComponent(url)}`
                    : url;

            loader.load(
                proxyUrl,
                (texture) => {
                    console.log("Texture loaded successfully");
                    resolve(texture);
                },
                undefined,
                (error) => {
                    console.error("Error loading texture:", error);
                    reject(error);
                },
            );
        });
    }

    /**
     * 3D click handler for close button
     */
    setupClickHandler() {
        const canvas = document.getElementById("viewer");
        if (!canvas) {
            console.warn(
                "Viewer canvas not found - cannot set up 3D close button click handler",
            );
            return;
        }

        canvas.addEventListener("click", (event) => {
            if (!this.isVisible || !this.closeButton) return;

            const rect = canvas.getBoundingClientRect();
            this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

            this.raycaster.setFromCamera(this.mouse, this.camera);

            const intersects = this.raycaster.intersectObject(
                this.closeButton,
                true,
            );

            if (intersects.length > 0) {
                console.log("3D close button clicked");
                this.stopSlideshow();
                this.hideImage();
            }
        });
    }

    setupKeyboardEvents() {
        document.addEventListener("keydown", (event) => {
            if (event.key === "Escape" && this.isVisible) {
                this.hideImage();
                this.stopSlideshow();
            }
        });
    }

    /**
     * Start an automatic slideshow through an array of images
     * Each image is shown for the specified duration before moving to the next
     * @param {Array<string>} imageUrls - Array of image URLs to cycle through
     * @param {number} displayDuration - How long to show each image in ms (default 5000)
     */
    startSlideshow(imageUrls, displayDuration = 5000) {
        if (!imageUrls || imageUrls.length === 0) {
            console.log("SimpleImagePanel: No images provided for slideshow");
            return;
        }

        console.log(`SimpleImagePanel: Starting slideshow with ${imageUrls.length} images, ${displayDuration}ms each`);

        // Stop any existing slideshow
        this.stopSlideshow();

        // Skip the first image if it's already displayed (prevents duplicate first image)
        let startIndex = 0;
        if (this._currentImageUrl && imageUrls[0] === this._currentImageUrl && this.isVisible) {
            console.log("SimpleImagePanel: Skipping first image (already displayed)");
            startIndex = 1;
            // If we only had one image and it's already shown, nothing to do
            if (imageUrls.length === 1) {
                console.log("SimpleImagePanel: Only one image and it's already displayed, no slideshow needed");
                return;
            }
        }

        // Initialize slideshow state with generation token for cancellation
        this._slideshowImages = [...imageUrls];
        this._slideshowIndex = startIndex;
        this._slideshowDuration = displayDuration;
        this._slideshowActive = true;
        this._slideshowTimeoutId = null;
        this._slideshowGeneration = (this._slideshowGeneration || 0) + 1; // Increment generation token

        // Start showing images
        this._showNextSlideshowImage(this._slideshowGeneration);
    }

    /**
     * Stop the current slideshow
     */
    stopSlideshow() {
        console.log("SimpleImagePanel: Stopping slideshow");

        this._slideshowActive = false;
        this._slideshowImages = [];
        this._slideshowIndex = 0;

        // Clear any pending timeout unconditionally
        if (this._slideshowTimeoutId) {
            clearTimeout(this._slideshowTimeoutId);
            this._slideshowTimeoutId = null;
        }
    }

    /**
     * Check if slideshow is currently active
     * @returns {boolean}
     */
    isSlideshowActive() {
        return this._slideshowActive === true;
    }

    /**
     * Internal: Show the next image in the slideshow sequence
     * @param {number} generation - Generation token to detect if slideshow was stopped/restarted
     */
    async _showNextSlideshowImage(generation) {
        // Check if slideshow was stopped or a new one started (generation mismatch)
        if (!this._slideshowActive || generation !== this._slideshowGeneration) {
            console.log("SimpleImagePanel: Slideshow cancelled (generation mismatch or stopped)");
            return;
        }

        // Check if we've shown all images (stop after one cycle)
        if (this._slideshowIndex >= this._slideshowImages.length) {
            console.log("SimpleImagePanel: Slideshow completed one cycle");
            this._slideshowActive = false;
            // Hide the last image after the slideshow completes
            this.hideImage();
            return;
        }

        const imageUrl = this._slideshowImages[this._slideshowIndex];
        console.log(`SimpleImagePanel: Showing slideshow image ${this._slideshowIndex + 1}/${this._slideshowImages.length}`);

        try {
            // Show the current image
            const success = await this.showImage(imageUrl);

            // Check again after async operation - slideshow may have been stopped during loading
            if (!this._slideshowActive || generation !== this._slideshowGeneration) {
                console.log("SimpleImagePanel: Slideshow stopped during image load");
                // Hide the image that just loaded since slideshow was cancelled
                this.hideImage();
                return;
            }

            if (!success) {
                console.warn(`SimpleImagePanel: Failed to show slideshow image ${this._slideshowIndex + 1}`);
            }

            // Only continue if slideshow is still active with same generation
            if (this._slideshowActive && generation === this._slideshowGeneration) {
                // Move to next image index
                this._slideshowIndex++;

                // Schedule the next image with generation token
                this._slideshowTimeoutId = setTimeout(() => {
                    this._showNextSlideshowImage(generation);
                }, this._slideshowDuration);
            }
        } catch (error) {
            console.error("SimpleImagePanel: Error in slideshow:", error);
            this.stopSlideshow();
        }
    }

}
